<template>
	<div class="hdl-register">
		<div class="hdl-auth-container mb-4">
			<div v-if="step === 'info'">
				<div class="mb-5 flex flex-row justify-center items-center">
					<img src="/static/images/logo.svg" width="32" alt="logo" class="auth-medley-logo" />
				</div>
				<div class="hdl-heading-secondary text-center mb-5">
					Sign up
				</div>
				<div class="mt-8 mb-4">
					<div id="g_id_onload"
						data-client_id="958561032847-73guq36h51p06oj2le8i5gddq48d691c.apps.googleusercontent.com"
						data-context="signin"
						data-ux_mode="redirect"
						:data-login_uri="`${baseDomain}/account/sso/google/`"
						data-auto_prompt="false">
					</div>

					<div class="g_id_signin"
						data-type="standard"
						data-shape="rectangular"
						data-theme="outline"
						data-text="continue_with"
						data-size="large"
						data-width="388"
						data-logo_alignment="left"
						:data-state="`register:${selectedPlan}`"
						ref="googleSignInButton"
					>
					</div>
				</div>
				<div class="text-hr mb-4"><span>OR</span></div>
				<div class="mb-5">
					<div class="mb-1 text-lg font-semibold">Name</div>
					<hdl-textbox
						v-model="name"
						hdl-style="auth"
						class="w-full"
						focus-on-mount
						@enter="() => { if (stepIsValid(step)) { nextStep(); } }"
					/>
				</div>
				<div class="mb-5">
					<div class="mb-1 text-lg font-semibold">Email</div>
					<hdl-textbox
						:model-value="email"
						@update:model-value="emailInput"
						hdl-style="auth"
						class="w-full"
						@enter="() => { if (stepIsValid(step)) { nextStep(); } }"
					/>
					<transition name="fade">
						<div
							class="hdl-subtext mt-1 flex flex-row items-center"
							v-if="emailValidation === 'exists'"
						>
							<font-awesome-icon
								class="text-hdl-yellow mr-2"
								:icon="['fas', 'triangle-exclamation']"
							/>
							An account with this email address already exists
						</div>
					</transition>
				</div>
				<div class="mb-5">
					<div class="mb-1 text-lg font-semibold">Choose a password (at least 8 characters)</div>
					<hdl-textbox
						v-model="password"
						hdl-style="auth"
						class="w-full"
						password
						@enter="() => { if (stepIsValid(step)) { nextStep(); } }"
					/>
				</div>
				<div class="mb-5">
					<div class="mb-1 text-lg font-semibold">Retype your password</div>
					<hdl-textbox
						v-model="passwordAgain"
						hdl-style="auth"
						class="w-full"
						password
						@enter="() => { if (stepIsValid(step)) { nextStep(); } }"
					/>
				</div>
				<div class="mb-8">
					<hdl-button
						@click.prevent="nextStep"
						:disabled="!stepIsValid(step)"
						label="Sign up"
						hdl-style="primary"
						size="lg"
						class="w-full"
					/>
				</div>
				<div class="text-center hdl-footnote-text">
					<div class="mb-2">
						Already have an account?
						<a
							href="#"
							@click.prevent="$router.push('/account/login/')"
						>Sign in</a>.
					</div>
				</div>
			</div>
			<div v-else-if="step === 'confirm'">
				<div class="text-center text-3xl mb-5">
					<font-awesome-icon :icon="['fass', 'envelope-circle-check']" />
				</div>
				<div class="hdl-heading-secondary text-center mb-2">
					Verify your email
				</div>
				<div class="text-center mb-5 text-lg">
					We've sent a 6-character code to {{ email }}. Enter it below.
				</div>
				<div class="mb-5 flex flex-col">
					<hdl-textbox
						hdl-style="auth"
						:model-value="confirmToken"
						@update:model-value="confirmTokenInput"
						focus-on-mount
						class="w-full"
						@enter="() => { if (stepIsValid(step)) { nextStep(); } }"
					/>
				</div>
				<div class="mb-5">
					<div
						class="flex flex-row items-center hdl-subtext justify-center"
						v-if="emailConfirmed === 'validated'"
					>
						<font-awesome-icon :icon="['fass', 'circle-check']" fixed-width class="text-hdl-green mr-2" />
						<div>Your email address has been confirmed</div>
					</div>
					<div
						class="flex flex-row items-center hdl-subtext justify-center"
						v-else-if="emailConfirmed === 'invalid'"
					>
						<font-awesome-icon :icon="['fass', 'circle-xmark']" fixed-width class="text-hdl-red mr-2" />
						<div>The provided token is not valid</div>
					</div>
				</div>
			</div>
			<div v-else-if="step === 'workspace'">
				<div class="text-center text-3xl mb-5">
					<font-awesome-icon :icon="['fass', 'circle-check']" />
				</div>
				<div class="hdl-heading-secondary text-center mb-2">
					Create your workspace
				</div>
				<div class="text-center mb-5 text-lg">
					This is where you'll create and analyze surveys.
				</div>
				<div class="mb-5">
					<div class="mb-1 text-lg font-semibold">Workspace name</div>
					<hdl-textbox
						v-model="workspaceName"
						hdl-style="auth"
						class="w-full"
						focus-on-mount
						@enter="() => { if (stepIsValid(step)) { completeRegistration(); } }"
					/>
				</div>
				<div class="mb-5 flex flex-col">
					<div class="mb-1 text-lg font-semibold">Select a plan</div>
					<PlanSelector v-model="selectedPlan" />
				</div>
			</div>
			<div v-if="step !== 'info'">
				<div class="mb-8">
					<hdl-button
						@click.prevent="nextStep"
						label="Continue"
						hdl-style="primary"
						size="lg"
						class="w-full"
						:disabled="!stepIsValid(step)"
						v-if="step !== 'workspace'"
					/>
					<hdl-button
						@click.prevent="completeRegistration"
						:disabled="!stepIsValid(step)"
						:label="(selectedPlan === 'free')? 'Create workspace' : 'Subscribe & create workspace'"
						hdl-style="primary"
						size="lg"
						class="w-full"
						v-else
					/>
					<div class="mt-5 hdl-footnote-text text-center" v-if="resendTokenAvailable && step === 'confirm'">
						Didn't get a code? Check your spam folder and if it's not there,
						<a
							@click.prevent="resendTokenEmail"
							class="cursor-pointer"
						>resend the email</a>.
					</div>
					<!--<hdl-button
						@click.prevent="prevStep"
						label="Go back"
						class="w-full mt-5"
						size="lg"
						v-if="step !== 'confirm'"
					/>-->
				</div>
			</div>
			<div class="hdl-footnote-text mt-4 text-center" v-if="step === 'info'">
				By signing up you agree to the
				<a href="https://medley.so/company/terms-of-service" target="_blank">Terms of Service</a> and
				acknowledge the <a href="https://medley.so/company/privacy-policy" target="_blank">Privacy Policy</a>.
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faHandWave, faIdCard } from '@fortawesome/pro-duotone-svg-icons';
import { faHouseHeart } from '@fortawesome/pro-light-svg-icons';
import { faCircleCheck, faCircleXmark, faTriangleExclamation, faEnvelopeCircleCheck } from '@fortawesome/sharp-solid-svg-icons';
import { fetcher, FetchResult } from '@/common/fetcher';
import { Choice } from '@/common/types';
import debounce from 'lodash.debounce';
import PlanSelector from '../components/PlanSelector.vue';
import { captchaVerified } from '@/common/utils';

library.add(
	faHandWave, faIdCard, faHouseHeart,
	faCircleCheck, faCircleXmark, faTriangleExclamation, faEnvelopeCircleCheck
);

declare type RegisterStep = 'info' | 'confirm' | 'workspace';

declare type RoleType = 'customer-experience' | 'product-management' | 'user-research' | 'marketing' | 'design' | 'engineering' | 'data-science' | 'other';

const RoleChoices: Array<Choice<RoleType>> = [
	{
		label: 'Customer experience',
		value: 'customer-experience',
	},
	{
		label: 'Product management',
		value: 'product-management'
	},
	{
		label: 'User research',
		value: 'user-research'
	},
	{
		label: 'Marketing',
		value: 'marketing',
	},
	{
		label: 'Design',
		value: 'design'
	},
	{
		label: 'Engineering',
		value: 'engineering'
	},
	{
		label: 'Data science',
		value: 'data-science',
	},
	{
		label: "My role isn't listed",
		value: 'other'
	}
];

declare type ValidationType = 'notvalidated' | 'validated' | 'invalid';
declare type EmailValidationType = ValidationType | 'exists';

@Component({
	components: {
		PlanSelector
	}
})
export default class RegisterPage extends Vue {
	email: string = "";
	name: string = "";
	password: string = "";
	passwordAgain: string = "";

	confirmToken: string = "";
	confirmingToken: boolean = false;
	resendTokenAvailable: boolean = false;

	selectedPlan: 'free' | 'pro' = 'free';

	emailValidation: EmailValidationType = 'notvalidated'; // Make sure a user with this email does not yet exist
	emailConfirmed: ValidationType = 'notvalidated'; // Token confirmation of email

	step: RegisterStep = 'info';

	role: RoleType | null = null;
	roleChoices: Array<Choice<RoleType>> = RoleChoices;

	workspaceName: string = '';

	debouncedValidateEmailAvailable: (() => void) | null = null;

	created(): void {
		this.debouncedValidateEmailAvailable = debounce(
			this.validateEmailAvailable, 300
		);

		if (this.$route.query) {
			if (this.$route.query.plan && ['free', 'pro'].includes(this.$route.query.plan as string)) {
				this.selectedPlan = this.$route.query.plan as 'free' | 'pro';
			}
		}
	}

	mounted(): void {
		this.rerenderGoogleButton();
	}

	rerenderGoogleButton(): void {
		const button = this.$refs.googleSignInButton as HTMLDivElement;
		//@ts-ignore
		google.accounts.id.renderButton(
			button,
			button.dataset
		);
	}

	nextStep(): void {
		switch(this.step) {
			case 'info':
				if (this.emailConfirmed !== 'validated') {
					this.sendEmailConfirm();
					this.step = 'confirm';
				} else {
					this.step = 'workspace';
				}
			break
			case 'confirm':
				this.step = 'workspace';
			break;
		}
	}

	prevStep(): void {
		switch(this.step) {
			case 'workspace':
				this.step = 'info';
			break;
		}
	}

	sendEmailConfirm(): void {
		if (!this.emailIsValid || this.emailValidation !== 'validated') {
			return;
		}

		fetcher.Put(
			`/account/register/email/confirm/?email=${encodeURIComponent(this.email)}`
		).then((result: FetchResult) => {
			if (result.response.ok && result.json.success) {
				setTimeout(
					() => this.resendTokenAvailable = true,
					10000
				);
			} else {
				this.resendTokenAvailable = true;
			}
		});
	}

	resendTokenEmail(): void {
		if (!this.resendTokenAvailable) {
			return;
		}

		this.resendTokenAvailable = false;

		this.sendEmailConfirm();
	}

	confirmEmailConfirmationToken(token: string): void {
		fetcher.Post(
			'/account/register/email/confirm/',
			{
				token,
				email: this.email
			}
		).then(
			(result: FetchResult) => {
				if (result.response.ok && result.json.success) {
					this.emailConfirmed = 'validated';
				} else if (result.response.ok) {
					this.emailConfirmed = 'invalid';
				} else {
					this.emailConfirmed = 'notvalidated';
				}

				this.confirmingToken = false;
			}
		);
	}

	confirmTokenInput(token: string): void {
		this.confirmToken = token;

		if (this.confirmToken.trim().length === 6 && !this.confirmingToken) {
			this.confirmingToken = true;

			this.confirmEmailConfirmationToken(
				this.confirmToken.trim()
			);
		}
	}

	get emailIsValid(): boolean {
		return this.email.trim().length > 5
			&& this.email.includes('@')
			&& !this.email.endsWith('.')
			&& !this.email.endsWith('@');
	}

	get isValidPassword(): boolean {
		if (this.password.trim().length < 8) {
			return false;
		}

		return this.password === this.passwordAgain;
	}

	stepIsValid(step: RegisterStep): boolean {
		switch(step) {
			case 'info':
				return (
					this.emailIsValid
					&& this.emailValidation === 'validated'
					&& this.name.trim().length > 0
					&& this.isValidPassword
				);
			case 'confirm':
				return this.emailConfirmed === 'validated';
			case 'workspace':
				return (
					this.workspaceName.trim().length > 0
				);
		}
	}

	completeRegistration(): void {
		if (
			(['info', 'confirm', 'workspace'] as Array<RegisterStep>).some(
				(step: RegisterStep) => !this.stepIsValid(step)
			)
		) {
			return;
		}

		let timezone: string | null = null;
		try {
			timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		} catch(e) {}

		//captchaVerified((recaptchaToken: string) => {
		fetcher.Post(
			'/account/register/',
			{
				email: this.email,
				token: this.confirmToken,
				name: this.name,
				password: this.password,
				workspaceName: this.workspaceName,
				timezone,
				plan: this.selectedPlan,
				//recaptchaToken
			}
		).then(
			(result: FetchResult) => {
				if (result.response.ok && result.json.success) {
					window.location.assign(
						result.json.data.redirectTo
					);
				}
			}
		);
		//});
	}

	emailInput(email: string): void {
		this.email = email;
		this.emailValidation = 'notvalidated';

		if (this.emailIsValid) {
			this.debouncedValidateEmailAvailable!();
		}
	}

	validateEmailAvailable(): void {
		if (!this.emailIsValid) {
			return;
		}

		fetcher.Get(
			`/account/register/email/?email=${encodeURIComponent(this.email)}`
		).then((result: FetchResult) => {
			if (result.response.ok && result.json.success) {
				this.emailValidation = result.json.data.valid as EmailValidationType;
			}
		});
	}

	get baseDomain(): string {
		return window.location.protocol + '//' + window.location.host;
	}
}
</script>

<style lang="scss">
.hdl-register {
	.token-input {
		width: 135px;
	}
}
</style>
